import { action, makeObservable, observable, computed } from "mobx";
import * as AuthService from "../services/AuthService";
import { logError } from "../components/logger";

class AuthStore {
  @observable currentUser: Record<string, unknown> = {};
  @observable firstName = "";
  @observable lastName = "";
  @observable email = "";
  @observable phone = "";
  @observable password = "";
  @observable accessToken = "";
  @observable userName = "";

  constructor() {
    makeObservable(this);
    // Initialize by fetching the token if available
    this.initializeAccessToken();
  }

  // Private action to initialize the token
  @action
  private initializeAccessToken(): void {
    AuthService.getAccessToken()
      .then(token => {
        if (token) {
          this.setAccessToken(token);
        }
      })
      .catch(error => {
        logError("Error initializing access token", error);
        console.error("Failed to initialize access token:", error);
      });
  }

  @action.bound
  setAccessToken(x: string): void {
    this.accessToken = x;
  }

  // Fixed by caching the observable value locally before async operations
  @action.bound
  async getAccessToken(): Promise<string> {
    // Cache the current token value locally
    const currentToken = this.accessToken;
    
    if (!currentToken) {
      const token = await AuthService.getAccessToken();
      // Use the action to modify the state
      this.setAccessToken(token || "");
      return this.accessToken;
    }
    
    return currentToken;
  }

  @action.bound
  async isLoggedIn(): Promise<boolean> {
    return (await this.getAccessToken()) !== "";
  }

  @computed
  get isLoggedInSync(): boolean {
    return !!this.accessToken;
  }

  @action.bound
  setCurrentUser(x: Record<string, string>): void {
    this.currentUser = x;
  }

  @action.bound
  setFirstName(x: string): void {
    this.firstName = x;
  }

  @action.bound
  setLastName(x: string): void {
    this.lastName = x;
  }

  @action.bound
  setEmail(x: string): void {
    this.email = x;
  }

  @action.bound
  setPhone(x: string): void {
    this.phone = x;
  }

  @action.bound
  setPassword(x: string): void {
    this.password = x;
  }

  @action.bound
  setUserName(x: string): void {
    this.userName = x;
  }
}

export const authStore = new AuthStore();